import { useQuery } from "@tanstack/react-query";
import { accounts } from "@/api/accounts";
import { useSession } from "./session";

/**
 * Who the signed-in operator is, for the platform screens. `isMaster`
 * comes from the login response; `isSuperduper` is the account
 * document's `metadata.superduper_admin`, the check crossbar itself uses
 * (a non-master account can carry it). Until the account answers,
 * `isSuperduper` is `undefined`, not false.
 */
export interface Operator {
  accountId?: string;
  accountName?: string;
  isReseller: boolean;
  isMaster: boolean;
  isSuperduper?: boolean;
  loading: boolean;
  /** The account read failed; superduper gates then resolve closed rather than staying open. */
  error?: boolean;
}

export function useOperator(): Operator {
  const session = useSession((s) => s.session);
  const q = useQuery({
    queryKey: ["account-detail", session?.accountId],
    queryFn: () => accounts.detail(session!.accountId),
    enabled: Boolean(session?.accountId),
    staleTime: 5 * 60_000,
  });
  // The login's `is_master_account` is a different store from the account's
  // `pvt_superduper_admin` (a demoted master keeps the first, loses the
  // second), and crossbar checks only the second; so never fall back to it.
  const superduper = q.data ? Boolean(q.data.meta.superduper_admin) : q.isError ? false : undefined;
  return {
    accountId: session?.accountId,
    accountName: session?.accountName,
    isReseller: Boolean(session?.isReseller),
    isMaster: Boolean(session?.isMaster),
    isSuperduper: superduper,
    loading: q.isLoading,
    error: q.isError,
  };
}

/** Whether a section's `requires` is met; unknown (still loading) counts as met so the column does not flicker. */
export function meetsRequirement(op: Operator, requires: "superduper" | "reseller" | undefined): boolean {
  if (!requires) return true;
  if (requires === "superduper") return op.isSuperduper !== false;
  return op.isReseller || op.isSuperduper !== false;
}
